import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {apiFetchPublic, ApiError} from '../api';
import Checklist from './Checklist';
import Spinner from './Spinner';
import {LockIcon} from './icons';

const SESSION_KEY_PREFIX = 'share_access_';

function getStoredAccessToken(token) {
    try {
        return sessionStorage.getItem(SESSION_KEY_PREFIX + token) || null;
    } catch {
        return null;
    }
}

function setStoredAccessToken(token, accessToken) {
    try {
        if (accessToken) {
            sessionStorage.setItem(SESSION_KEY_PREFIX + token, accessToken);
        } else {
            sessionStorage.removeItem(SESSION_KEY_PREFIX + token);
        }
    } catch {
        // sessionStorage not available
    }
}

export default function SharedChecklistPage() {
    const {token} = useParams();
    const [checklist, setChecklist] = useState(null);
    const [accessLevel, setAccessLevel] = useState('read');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [requiresPin, setRequiresPin] = useState(false);
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState(null);
    const [accessToken, setAccessToken] = useState(() => getStoredAccessToken(token));
    const [verifying, setVerifying] = useState(false);

    const fetchChecklist = useCallback(
        async at => {
            setLoading(true);
            setError(null);
            try {
                const headers = {};
                if (at) headers['X-Share-Access'] = at;
                const res = await apiFetchPublic(`/s/${token}`, {headers, includeAuth: true});
                const data = await res.json();
                setChecklist(data);
                setAccessLevel(data.accessLevel || 'read');
                setRequiresPin(false);
                setLoading(false);
            } catch (err) {
                if (err instanceof ApiError && err.status === 401) {
                    // Access token expired or PIN required
                    if (at) {
                        setStoredAccessToken(token, null);
                        setAccessToken(null);
                    }
                    setRequiresPin(true);
                    setLoading(false);
                    return;
                }
                if (err instanceof ApiError && err.status === 404) {
                    setError('not_found');
                } else {
                    setError('error');
                }
                setLoading(false);
            }
        },
        [token],
    );

    useEffect(() => {
        fetchChecklist(accessToken);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    async function handleVerifyPin() {
        if (pinInput.length < 4 || verifying) return;
        setVerifying(true);
        setPinError(null);
        try {
            const res = await apiFetchPublic(`/s/${token}/verify`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({pin: pinInput}),
                includeAuth: true,
            });
            const data = await res.json();
            setAccessToken(data.accessToken);
            setStoredAccessToken(token, data.accessToken);
            setPinInput('');
            fetchChecklist(data.accessToken);
        } catch (err) {
            if (err instanceof ApiError && err.status === 429) {
                setPinError('Too many attempts. Please wait a minute.');
            } else {
                setPinError('Incorrect PIN. Please try again.');
            }
        } finally {
            setVerifying(false);
        }
    }

    const handleToggleItem = useCallback(
        async (_checklistId, itemId) => {
            try {
                const headers = {};
                if (accessToken) headers['X-Share-Access'] = accessToken;
                const res = await apiFetchPublic(`/s/${token}/items/${itemId}`, {
                    method: 'PATCH',
                    headers,
                    includeAuth: true,
                });
                const updated = await res.json();
                setChecklist(prev => ({
                    ...prev,
                    items: prev.items.map(i => (i.id === itemId ? {...i, checked: updated.checked} : i)),
                }));
            } catch {
                // silently fail — user can retry
            }
        },
        [token, accessToken],
    );

    if (loading) {
        return (
            <div className="shared-page" data-testid="shared-page">
                <Spinner size={32} className="spinner-fullpage" />
            </div>
        );
    }

    if (requiresPin) {
        return (
            <div className="shared-page" data-testid="shared-page">
                <div className="shared-pin-entry" data-testid="pin-entry">
                    <div className="shared-pin-icon">
                        <LockIcon size={32} />
                    </div>
                    <h2>This checklist is PIN protected</h2>
                    <p>Enter the PIN to view this checklist.</p>
                    <input
                        className="shared-pin-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        placeholder="Enter PIN"
                        value={pinInput}
                        onChange={e => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onKeyDown={e => e.key === 'Enter' && handleVerifyPin()}
                        autoFocus
                        data-testid="pin-input"
                    />
                    <button
                        className="shared-pin-submit"
                        onClick={handleVerifyPin}
                        disabled={pinInput.length < 4 || verifying}
                        data-testid="pin-submit"
                    >
                        {verifying ? 'Verifying...' : 'Unlock'}
                    </button>
                    {pinError && (
                        <p className="shared-pin-error" data-testid="pin-error">
                            {pinError}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    if (error === 'not_found') {
        return (
            <div className="shared-page" data-testid="shared-page">
                <div className="shared-error">
                    <h2>Not available</h2>
                    <p>This checklist is no longer available.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="shared-page" data-testid="shared-page">
                <div className="shared-error">
                    <h2>Something went wrong</h2>
                    <p>Could not load this checklist. Please try again later.</p>
                </div>
            </div>
        );
    }

    const isReadOnly = accessLevel === 'read';
    const noop = () => {};

    return (
        <div className="shared-page" data-testid="shared-page">
            <div className="shared-banner">
                You&rsquo;re viewing a shared checklist
                {!isReadOnly && ' (you can toggle items)'}
            </div>
            <div className="shared-checklist-wrapper">
                <Checklist
                    data={checklist}
                    readOnly={isReadOnly}
                    shareEditMode={!isReadOnly}
                    onToggleItem={handleToggleItem}
                    onDelete={noop}
                    onAddItem={noop}
                    onDeleteItem={noop}
                    onUpdateTitle={noop}
                    onUpdateItem={noop}
                    onReorderItems={noop}
                    onToggleSortMode={noop}
                    onToggleSinkChecked={noop}
                    onToggleHideChecked={noop}
                    onStarItem={noop}
                    onSetDueDateItem={noop}
                    isFocused={false}
                    onFocus={noop}
                    onUnfocus={noop}
                />
            </div>
        </div>
    );
}
